import type { EntryPointRouteParams } from '@jira/platform__entry-points-plugin-types/src/types.tsx';
import { cleansingCategoriesEntryPoint } from '@jira/assets-app__data-manager-object-classes-settings/src/ui/cleansing-categories/entrypoint.tsx';
import { dataSourceTypesEntryPoint } from '@jira/assets-app__data-manager-object-classes-settings/src/ui/data-source-types/entrypoint.tsx';
import { JSResourceForInteraction } from '@atlassian/react-async';
import { createEntryPoint } from '@atlassian/react-entrypoint';
import type { ObjectClassesSettingsType } from '@atlassian/jira-assets-data-manager-common/src/common/types.tsx';

const SETTING_TYPES: readonly ObjectClassesSettingsType[] = [
	'data-source-type',
	'data-source-priorities',
	'attribute-priorities',
	'default-cleansing-rules',
	'cleansing-categories',
] as const;

// TODO: Import and add entrypoints for each setting type when they are created
const SETTINGS: Partial<Record<ObjectClassesSettingsType, typeof cleansingCategoriesEntryPoint>> = {
	'cleansing-categories': cleansingCategoriesEntryPoint,
	'data-source-type': dataSourceTypesEntryPoint,
};

const isValidSettingType = (settingType: unknown): settingType is ObjectClassesSettingsType => {
	if (typeof settingType !== 'string') {
		return false;
	}
	return SETTING_TYPES.some((validType) => validType === settingType);
};

const getEntryPoint = (
	settingType: ObjectClassesSettingsType,
): typeof cleansingCategoriesEntryPoint | undefined => {
	return SETTINGS[settingType];
};

export const assetsDataManagerObjectClassesSettingsEntryPoint = createEntryPoint({
	root: JSResourceForInteraction(
		() =>
			import(
				/* webpackChunkName: "async-assets-data-manager-object-classes-settings", jiraSpaEntry: "async-assets-data-manager-object-classes-settings" */ './src'
			),
	),
	getPreloadProps: ({
		context: {
			match: { params },
		},
	}: EntryPointRouteParams) => {
		const validSettingType = isValidSettingType(params?.settingType)
			? params.settingType
			: undefined;

		if (validSettingType) {
			const entryPoint = getEntryPoint(validSettingType);
			if (entryPoint) {
				return {
					entryPoints: {
						[validSettingType]: {
							entryPoint,
							entryPointParams: {},
						},
					},
					extraProps: {
						settingType: validSettingType,
					},
				};
			}
		}

		return {
			entryPoints: {},
			extraProps: {
				settingType: validSettingType,
			},
		};
	},
});
